import React, { useEffect, useState } from 'react'
import { Checkbox } from './Checkbox'

export type CheckboxOption = {
  name: string
  value: string
  checked: boolean
}

export type CheckboxListProps = {
  legend?: string
  message?: string
  helpText?: string
  error?: boolean
  reference?: React.RefObject<HTMLInputElement>
  options?: CheckboxOption[]
  onOptionsSelected?: (selectedOptions: CheckboxOption[]) => void
  testId?: string
}

export const CheckboxList = (props: CheckboxListProps) => {
  const {
    legend,
    message,
    helpText,
    error,
    reference,
    options,
    onOptionsSelected,
    testId
  } = props
  const [checkedState, setCheckedState] = useState<CheckboxOption[]>(
    options ?? []
  )

  const handleChange = (name: string) => {
    const modifiedOptions = checkedState.map(item =>
      name === item.name ? { ...item, checked: !item.checked } : item
    )

    setCheckedState(modifiedOptions)
  }

  useEffect(() => {
    onOptionsSelected?.(checkedState)
  }, [checkedState, onOptionsSelected])

  useEffect(() => {
    setCheckedState(options ?? [])
  }, [options])

  const describedByIds = [
    error && message ? 'errorContainer' : '',
    helpText ? 'helpContainer' : ''
  ]
    .filter(Boolean)
    .join(' ')

  const list = (
    <ul
      className="checkbox-list"
      data-testid={testId ? `${testId}-list` : undefined}
    >
      {checkedState.map((option, index) => {
        const inputId = `custom-checkbox-${option.name}`

        return (
          <li key={`${option.name}-${index}`}>
            <Checkbox
              id={inputId}
              name={option.name}
              value={option.value}
              label={option.value}
              checked={option.checked}
              onChange={() => handleChange(option.name)}
              reference={reference}
              testId={testId}
            />
          </li>
        )
      })}
    </ul>
  )

  return (
    <fieldset
      className="Form-group"
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedByIds || undefined}
      data-testid={testId ? `${testId}-fieldset` : undefined}
    >
      {legend && <legend className="u-marginBottom8">{legend}</legend>}
      {error ? (
        <div className="RadioCheckbox-wrapper Input--error">{list}</div>
      ) : (
        list
      )}
      {error && message && (
        <ul id="errorContainer">
          <li
            className="Form-help Form-help--error"
            data-testid={testId ? `${testId}-message` : undefined}
          >
            <span
              data-testid={testId ? `${testId}-warning` : undefined}
              aria-hidden="true"
            >
              warning
            </span>
            <span className="u-hiddenVisually">Error,</span>
            <span>{message}</span>
          </li>
        </ul>
      )}
      {helpText && (
        <ul id="helpContainer">
          <li
            className="Form-help"
            data-testid={testId ? `${testId}-helpText` : undefined}
          >
            {helpText}
          </li>
        </ul>
      )}
    </fieldset>
  )
}
